import { createDefaultProductApi, updateDefaultProductApi, useGetTaxRateList } from "@/api/services/global/master";
import { TGetDefaultProductListOutputDto } from "@/api/services/global/master/dtos/defaultProduct/get-master-default-product-list.dto";
import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { Select } from "@/components/elements/Select";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { masterDataDefaultProductSchema } from "@/features/master-data/schema/masterDataDefaultProductSchema";
import { TMasterDefaultProductForm } from "@/features/master-data/types/masterDataDefaultProductForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TDefaultProductFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedDefaultProduct?: TGetDefaultProductListOutputDto;
  /** 商品名の重複を送信前にチェックするための既存行 */
  defaultProducts: TGetDefaultProductListOutputDto[];
};

export const DefaultProductFormModal: FC<TDefaultProductFormModalProps> = ({ onClose, isOpen, selectedDefaultProduct, defaultProducts }) => {
  const queryClient = useQueryClient();
  const { taxRateOptions, isLoading: isLoadingTaxRate } = useGetTaxRateList();

  const defaultValues = useMemo(
    () => ({
      productName: selectedDefaultProduct ? selectedDefaultProduct.productName : undefined,
      unit: selectedDefaultProduct ? selectedDefaultProduct.unit : undefined,
      taxRateId: selectedDefaultProduct ? toNumber(selectedDefaultProduct.taxRateId) : undefined,
      isContractLinked: selectedDefaultProduct ? selectedDefaultProduct.isContractLinked : false,
      invoiceDisplayFormat: selectedDefaultProduct?.invoiceDisplayFormat ?? null,
      billingCycle: selectedDefaultProduct ? selectedDefaultProduct.billingCycle : undefined,
      isTierDiscountTarget: selectedDefaultProduct ? selectedDefaultProduct.isTierDiscountTarget : false,
      accountingItemMapping: selectedDefaultProduct?.accountingItemMapping ?? null,
    }),
    [selectedDefaultProduct],
  );

  const methods = useForm<TMasterDefaultProductForm>({
    resolver: yupResolver(masterDataDefaultProductSchema),
    mode: "onBlur",
    defaultValues,
  });

  const { control, setError } = methods;

  /** 商品名は重複不可（サーバー側でも 409 で弾かれる） */
  const isDuplicatedProductName = (productName: string) =>
    defaultProducts.some((item) => item.productName === productName && toNumber(item.id) !== toNumber(selectedDefaultProduct?.id));

  const onSubmit = async (data: TMasterDefaultProductForm) => {
    if (isDuplicatedProductName(data.productName)) {
      setError("productName", { message: `「商品名」（${data.productName}）はすでに登録されています。` });
      return;
    }

    try {
      const payload = {
        productName: data.productName,
        unit: data.unit,
        taxRateId: toNumber(data.taxRateId),
        isContractLinked: Boolean(data.isContractLinked),
        invoiceDisplayFormat: data.invoiceDisplayFormat ?? null,
        billingCycle: data.billingCycle,
        isTierDiscountTarget: Boolean(data.isTierDiscountTarget),
        accountingItemMapping: data.accountingItemMapping ?? null,
      };

      if (selectedDefaultProduct) {
        const rs = await updateDefaultProductApi(toNumber(selectedDefaultProduct.id), payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createDefaultProductApi(payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_DEFAULT_PRODUCT] });
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal
      title={selectedDefaultProduct ? "編集" : "追加"}
      isOpen={isOpen}
      enableFooter={false}
      onClose={onClose}
      enableCloseIcon={false}
      width={560}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataDefaultProductSchema}>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={6}>
            <FormField control={control} name="productName">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            {/* 単位は暫定的に自由入力（例: 人／件／社） */}
            <FormField control={control} name="unit">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            {/* 選択肢は税率マスタから取得。無効化済みの税率は選択肢に出ない */}
            <FormField control={control} name="taxRateId">
              <Select options={taxRateOptions} loading={isLoadingTaxRate} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="billingCycle">
              <Select enumName="EBillingCycle" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="invoiceDisplayFormat" helper="未選択のままでも保存できます。">
              <Select enumName="EInvoiceDisplayFormat" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="accountingItemMapping">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="isContractLinked" sx={{ margin: 0 }}>
              <Checkbox label="契約紐付けの対象にする" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="isTierDiscountTarget" sx={{ margin: 0 }}>
              <Checkbox label="ティア・初回割引の対象にする" />
            </FormField>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="space-between" gap="24px" mt={3}>
          <Button size="sm" variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button size="sm" type="submit">
            保存
          </Button>
        </Stack>
      </Form>
    </Modal>
  );
};
